import type {
  ICartItem,
  IshipAddres,
  MaterialPriceAndImgForCart,
  PaymentIntentBootstrapIssue,
  PaymentIntentData,
  PaymentIntentInFlightRequest,
  PaymentIntentRequestKeyArgs,
  ZipTaxAddressInput,
} from '../../interfaces';

let inFlightPaymentIntentRequest: PaymentIntentInFlightRequest | null = null;

export const getCartMaterialDataByItemId = (
  materialPriceAndImgForCart: MaterialPriceAndImgForCart[]
) =>
  Object.fromEntries(
    materialPriceAndImgForCart.map((entry) => [entry.itemId, entry])
  );

export const createPaymentIntentRequestKey = ({
  cartItems,
  shipping,
  guestEmail,
  isGuestCheckout,
}: PaymentIntentRequestKeyArgs) =>
  JSON.stringify({
    cartItems: cartItems.map((item: ICartItem) => ({
      id: item.id,
      quantity: item.quantity,
      selectedMaterial: item.selectedMaterial,
      selectedSize: item.selectedSize,
    })),
    shipping: {
      firstName: shipping?.firstName || '',
      lastName: shipping?.lastName || '',
      addressLine1: shipping?.addressLine1 || '',
      addressLine2: shipping?.addressLine2 || '',
      city: shipping?.city || '',
      province: shipping?.province || '',
      postcode: shipping?.postcode || '',
      country: shipping?.country || '',
      telephone: shipping?.telephone || '',
      phoneCode: shipping?.phoneCode || '',
      emoji: shipping?.emoji || '',
      useAddressForBilling: shipping?.useAddressForBilling ?? true,
    },
    guestEmail: isGuestCheckout ? guestEmail : '',
  });

export const getPaymentIntentShippingAddress = (
  shipping?: IshipAddres | null
): ZipTaxAddressInput | undefined => {
  if (!shipping?.addressLine1 || !shipping.postcode) return undefined;

  return {
    firstName: shipping.firstName || undefined,
    lastName: shipping.lastName || undefined,
    addressLine1: shipping.addressLine1,
    addressLine2: shipping.addressLine2 || undefined,
    city: shipping.city || undefined,
    province: shipping.province || undefined,
    postcode: shipping.postcode,
    country: shipping.country || undefined,
    telephone: shipping.telephone || undefined,
    phoneCode: shipping.phoneCode || undefined,
    emoji: shipping.emoji || undefined,
    useAddressForBilling: shipping.useAddressForBilling ?? true,
  };
};

export const getPaymentIntentBootstrapError = ({
  cartItems,
  cartValidationIssues,
  hasStripePromise,
}: {
  cartItems: ICartItem[];
  cartValidationIssues: PaymentIntentBootstrapIssue[];
  hasStripePromise: boolean;
}) => {
  if (!cartItems.length) return 'Cart is empty.';
  if (cartValidationIssues.length > 0) return cartValidationIssues[0].message;
  if (!hasStripePromise) return 'Stripe publishable key is missing.';

  return null;
};

export const getOrCreatePaymentIntentRequest = (
  key: string,
  createRequest: () => Promise<PaymentIntentData>
) => {
  if (!inFlightPaymentIntentRequest || inFlightPaymentIntentRequest.key !== key) {
    const promise = createRequest();

    inFlightPaymentIntentRequest = { key, promise };

    void promise
      .finally(() => {
        if (inFlightPaymentIntentRequest?.promise === promise) {
          inFlightPaymentIntentRequest = null;
        }
      })
      .catch(() => undefined);
  }

  return inFlightPaymentIntentRequest.promise;
};
